/** Year-1 TotalMonthlyPayment from a LightReach Battery Only offer (GetPricingLRBO / grid payload). */
export function year1TotalMonthlyFromLrBoOffer(offer: unknown): number | null {
    if (!offer || typeof offer !== 'object') return null;
    const o = offer as Record<string, unknown>;
    const payments = o.MonthlyPayments ?? o.monthlyPayments;
    if (Array.isArray(payments) && payments.length > 0) {
        const y1 = payments.find((p) => p && Number((p as { Year?: unknown; year?: unknown }).Year ?? (p as { year?: unknown }).year) === 1);
        const row = (y1 ?? payments[0]) as Record<string, unknown>;
        const total = Number(row.TotalMonthlyPayment ?? row.totalMonthlyPayment);
        if (Number.isFinite(total) && total > 0) return total;
        const monthly = Number(row.MonthlyPayment ?? row.monthlyPayment);
        if (Number.isFinite(monthly) && monthly > 0) return monthly;
    }
    const nested = o.payload;
    if (nested && typeof nested === 'object') return year1TotalMonthlyFromLrBoOffer(nested);
    return null;
}

export function productIdFromLrBoOffer(offer: unknown): string | null {
    if (!offer || typeof offer !== 'object') return null;
    const id = (offer as { ProductId?: unknown; productId?: unknown }).ProductId
        ?? (offer as { productId?: unknown }).productId;
    if (id == null || String(id).trim() === '') return null;
    return String(id);
}

export function lrBoOffersFromResponse(res: unknown): any[] {
    if (Array.isArray(res)) return res;
    if (res && typeof res === 'object') {
        const data = (res as { Data?: unknown; data?: unknown }).Data
            ?? (res as { data?: unknown }).data;
        if (Array.isArray(data)) return data;
    }
    return [];
}

export function batteryTermAprOrNull(apr: unknown): string | null {
    const n = Number(apr);
    if (!Number.isFinite(n)) return null;
    return String(n);
}

/** V1 GetPricingLRBO fields (ASP.NET PascalCase or Lightreach camelCase). */
export function lrboEscalationPct(item: any): number {
    const rate = item?.EscalationRate ?? item?.escalationRate;
    return Number((Number(rate) * 100).toFixed(2));
}

export function lrboTerm(item: any): number {
    return Number(item?.term ?? item?.Term);
}

export function lrboMonthly(item: any): number {
    return year1TotalMonthlyFromLrBoOffer(item) ?? Number.NaN;
}

export function lrboProductId(item: any): string {
    return productIdFromLrBoOffer(item) ?? '';
}

export function matchLrboProduct(products: any[] | undefined, escalator: unknown, term: unknown) {
    if (!products?.length) return undefined;
    return products.find(
        (item: any) => lrboEscalationPct(item) == Number(escalator) && lrboTerm(item) == Number(term),
    );
}

/** Grid/solar escalator 0 is not a priced Lightreach offer (V1 ignores it). */
function positiveEscalatorOption(
    options: { value: string; label: string }[],
    solarEscalator: string | null | undefined,
): string | null {
    const solar = String(solarEscalator ?? '').trim();
    const solarN = Number(solar);
    if (solar && Number.isFinite(solarN) && solarN > 0) {
        const matched = options.find((x) => Number(x.value) === solarN);
        if (matched) return String(matched.value);
        return solar;
    }
    const firstPositive = options.find((x) => Number(x.value) > 0);
    if (firstPositive) return String(firstPositive.value);
    return options[0]?.value ?? null;
}

/** V1 battery-detail-form `setEscalator()`. */
export function pickBatteryEscalatorLikeV1(
    isLightreachBatteryLease: boolean,
    products: any[] | undefined,
    options: { value: string; label: string }[],
    solarEscalator: string | null | undefined,
): string | null {
    if (isLightreachBatteryLease) {
        const lightreachEscalators = products?.length
            ? products.map((item: any) => lrboEscalationPct(item))
            : [];
        const commonEscalators = lightreachEscalators.filter((rate) =>
            Number.isFinite(rate) && rate > 0 && options.some((x) => Number(x.value) === rate),
        );
        const highestEscalator = commonEscalators.length > 0 ? Math.max(...commonEscalators) : null;
        const defaultEscalator =
            highestEscalator !== null
                ? options.find((x) => Number(x.value) === highestEscalator)
                : null;
        if (defaultEscalator) return String(defaultEscalator.value);
        return positiveEscalatorOption(options, solarEscalator);
    }
    return positiveEscalatorOption(options, solarEscalator);
}
